// Read-only overall standings + final podium for the public share link.
// Aggregates every group-stage match into one ranked table (via the pure domain
// helpers) and shows a glowing gold/silver/bronze podium once the bracket
// decides it. Styling follows the "sleek dark + glow" direction.

import { useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import type {
  FootballStanding,
  PodiumEntry,
  Standing,
  TournamentState,
} from '../../domain';
import {
  computeFootballStandings,
  computeOverallStandings,
  computePodium,
} from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { glowCard } from './publicStyles';

interface PublicRanksProps {
  tournament: TournamentState;
}

interface RankRow extends Standing {
  name: string;
}

interface FootballRankRow extends FootballStanding {
  name: string;
}

const MEDAL: Record<1 | 2 | 3, { labelKey: string; color: string; glow: string }> = {
  1: { labelKey: 'medal.champion', color: '#FFD700', glow: 'rgba(255,215,0,0.45)' },
  2: { labelKey: 'medal.runnerUp', color: '#C0C0C0', glow: 'rgba(192,192,192,0.35)' },
  3: { labelKey: 'medal.third', color: '#CD7F32', glow: 'rgba(205,127,50,0.35)' },
};

export function PublicRanks({ tournament }: PublicRanksProps) {
  const { t } = useTranslation();
  const isFootball = tournament.sportId === 'FOOTBALL';
  const rows = useMemo<RankRow[]>(
    () =>
      isFootball
        ? []
        : computeOverallStandings(tournament).map((s) => ({
            ...s,
            name: tournament.players[s.playerId]?.name ?? s.playerId,
          })),
    [tournament, isFootball],
  );
  const footballRows = useMemo<FootballRankRow[]>(
    () =>
      isFootball
        ? computeFootballStandings(
            Object.keys(tournament.players),
            Object.values(tournament.cells),
          ).map((s) => ({
            ...s,
            name: tournament.players[s.playerId]?.name ?? s.playerId,
          }))
        : [],
    [tournament, isFootball],
  );
  const podium = useMemo<PodiumEntry[]>(() => computePodium(tournament), [tournament]);
  const championDecided = podium[0]?.playerId != null;

  const nameOf = (id: PodiumEntry['playerId']) =>
    id ? tournament.players[id]?.name ?? id : '—';

  if ((isFootball ? footballRows : rows).length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('ranks.empty')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      {championDecided && (
        <Box>
          {podium[0] && <PodiumCard place={1} name={nameOf(podium[0].playerId)} featured t={t} />}
          <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
            {podium[1] && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <PodiumCard place={2} name={nameOf(podium[1].playerId)} t={t} />
              </Box>
            )}
            {podium[2] && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <PodiumCard place={3} name={nameOf(podium[2].playerId)} t={t} />
              </Box>
            )}
          </Stack>
        </Box>
      )}

      <Paper elevation={0} sx={[{ p: 2.25 }, glowCard(false)]}>
        <Typography
          variant="overline"
          sx={{ display: 'block', mb: 1, color: 'text.secondary', letterSpacing: 1 }}
        >
          {t('ranks.overall')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isFootball
              ? '34px 1fr 34px 34px 40px 46px'
              : '34px 1fr 38px 38px 46px',
            rowGap: 0.25,
            columnGap: 1,
            alignItems: 'center',
          }}
        >
          <HeadLabel align="center">{t('table.rank')}</HeadLabel>
          <HeadLabel>{isFootball ? t('table.team') : t('table.player')}</HeadLabel>
          <HeadLabel align="right">{t('table.matchesPlayed')}</HeadLabel>
          {isFootball ? (
            <>
              <HeadLabel align="right">{t('table.won')}</HeadLabel>
              <HeadLabel align="right">{t('table.goalDiff')}</HeadLabel>
              <HeadLabel align="right">{t('table.points')}</HeadLabel>
            </>
          ) : (
            <>
              <HeadLabel align="right">{t('table.wins')}</HeadLabel>
              <HeadLabel align="right">{t('table.pointsWon')}</HeadLabel>
            </>
          )}
          {(isFootball ? footballRows : rows).map((row) => {
            const top = row.rank <= 3;
            return (
              <Box
                key={row.playerId}
                sx={{
                  display: 'contents',
                  '& > *': { py: 0.6, bgcolor: top ? 'rgba(255,255,255,0.035)' : 'transparent' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', borderTopLeftRadius: 8, borderBottomLeftRadius: 8 }}>
                  {top ? (
                    <Chip
                      size="small"
                      label={row.rank}
                      color={row.rank === 1 ? 'primary' : row.rank === 2 ? 'secondary' : 'default'}
                      sx={{ height: 20, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">
                      {row.rank}
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" noWrap sx={{ fontWeight: top ? 700 : 400, minWidth: 0 }}>
                  {row.name}
                </Typography>
                <Num>{row.matchesPlayed}</Num>
                {'points' in row ? (
                  <>
                    <Num>{row.matchesWon}</Num>
                    <Num>{row.goalDiff > 0 ? `+${row.goalDiff}` : row.goalDiff}</Num>
                    <Box sx={{ borderTopRightRadius: 8, borderBottomRightRadius: 8 }}>
                      <Num>{row.points}</Num>
                    </Box>
                  </>
                ) : (
                  <>
                    <Num>{row.matchesWon}</Num>
                    <Box sx={{ borderTopRightRadius: 8, borderBottomRightRadius: 8 }}>
                      <Num>{row.pointsWon}</Num>
                    </Box>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          {isFootball ? t('ranks.footballLegend') : t('ranks.legend')}
        </Typography>
      </Paper>
    </Stack>
  );
}

function PodiumCard({
  place,
  name,
  featured = false,
  t,
}: {
  place: 1 | 2 | 3;
  name: string;
  featured?: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const medal = MEDAL[place];
  return (
    <Paper
      elevation={0}
      sx={{
        p: featured ? 2.25 : 1.75,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 0,
        border: '1px solid',
        borderColor: featured ? `${medal.color}80` : 'rgba(255,255,255,0.1)',
        backgroundImage: `linear-gradient(135deg, ${medal.glow}, rgba(255,255,255,0))`,
        boxShadow: featured ? `0 16px 40px -16px ${medal.glow}` : `0 10px 26px -18px ${medal.glow}`,
      }}
    >
      {featured ? (
        <EmojiEventsIcon sx={{ color: medal.color, fontSize: 38, filter: `drop-shadow(0 0 8px ${medal.glow})` }} />
      ) : (
        <MilitaryTechIcon sx={{ color: medal.color, fontSize: 26 }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="overline"
          sx={{ color: medal.color, letterSpacing: 1, lineHeight: 1.3, display: 'block', fontWeight: 700 }}
        >
          {t(medal.labelKey)}
        </Typography>
        <Typography variant={featured ? 'h6' : 'subtitle2'} noWrap sx={{ fontWeight: 800, minWidth: 0 }}>
          {name}
        </Typography>
      </Box>
    </Paper>
  );
}

function HeadLabel({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'center' | 'right' }) {
  return (
    <Typography variant="caption" color="text.secondary" align={align} sx={{ fontWeight: 700, opacity: 0.85 }}>
      {children}
    </Typography>
  );
}

function Num({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" align="right" sx={{ pr: 0.5 }}>
      {children}
    </Typography>
  );
}
